import { useDisclosure } from "@heroui/react";
import { InputField, ModalBuilder, ModalError } from "../modalBuilder";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useState } from "react";
import { api } from "@/api";
import { toast } from "react-toastify";
import { Requisite } from "@/api/requisites/requisites.ts";
import { loadHandbooks } from "@/utils/loadHandbooks";

interface IEditRequisiteForm {
  displayName: string;
  inn: number;
  kpp: string;
  ogrn: string;
  bankAccount: string;
  bankName: string;
  bik: string;
  correspondentAccount: string;
  address: string;
  phone: string;
  email: string;
  currency: number;
  billable: boolean;
}

const editRequisiteFields: InputField[] = [
  {
    label: "Название",
    name: "displayName",
    placeholder: "Название счёта",
    type: "text",
    isRequired: true,
  },
  {
    label: "ИНН",
    name: "inn",
    placeholder: "1234567890",
    type: "text",
    isRequired: true,
  },
  {
    label: "КПП",
    name: "kpp",
    placeholder: "123456789",
    type: "text",
    isRequired: false,
  },
  {
    label: "ОГРН",
    name: "ogrn",
    placeholder: "1234567890123",
    type: "text",
    isRequired: false,
  },
  {
    label: "Расчетный счет",
    name: "bankAccount",
    placeholder: "12345678901234567890",
    type: "text",
    isRequired: true,
  },
  {
    label: "Название банка",
    name: "bankName",
    placeholder: "Название банка",
    type: "text",
    isRequired: true,
  },
  {
    label: "Валюта",
    name: "currency",
    placeholder: "Валюта",
    type: "currency",
    isRequired: true,
  },
  {
    label: "БИК",
    name: "bik",
    placeholder: "123456789",
    type: "text",
    isRequired: false,
  },
  {
    label: "Корреспондентский счет",
    name: "correspondentAccount",
    placeholder: "12345678901234567890",
    type: "text",
    isRequired: true,
  },
  {
    label: "Адрес",
    name: "address",
    placeholder: "Адрес компании",
    type: "text",
    isRequired: true,
  },
  {
    label: "Телефон",
    name: "phone",
    placeholder: "+71234567890",
    type: "text",
    isRequired: true,
  },
  {
    label: "Email",
    name: "email",
    placeholder: "email@domain.com",
    type: "text",
    isRequired: true,
  },
  {
    label: "Генерировать счета",
    name: "billable",
    type: "boolean",
    placeholder: "Генерировать счета",
    isRequired: false,
    tooltip:
      "Влияет на автоматическую генерацию документов при выставлении счетов",
  },
];

export function EditRequisiteModal({
  disclosure,
  requisite,
}: {
  disclosure: ReturnType<typeof useDisclosure>;
  requisite: Requisite | null;
}) {
  const { isOpen, onOpenChange } = disclosure;
  const [modalErrors, setModalErros] = useState<ModalError[]>([]);

  const handleError = useErrorHandling();

  const onSubmit = (data: IEditRequisiteForm) => {
    return new Promise<boolean>(async (resolve, _reject) => {
      api.requisites
        .update(requisite!.id, data)
        .then((_data) => {
          loadHandbooks({ handbooks: ["requisites"] });
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onDelete = () => {
    return new Promise<boolean>(async (resolve, _reject) => {
      api.requisites
        .delete(requisite!.id)
        .then((_data) => {
          loadHandbooks({ handbooks: ["requisites"] });
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  return (
    <ModalBuilder
      title="Редактирование счёта"
      modalErrors={modalErrors}
      fields={editRequisiteFields}
      isOpen={isOpen}
      isSensitiveData={true}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      onDelete={onDelete}
      submitButtonText="Сохранить"
      defaultValues={{
        displayName: requisite?.displayName,
        inn: requisite?.inn,
        kpp: requisite?.kpp != null ? String(requisite.kpp) : undefined,
        ogrn: requisite?.ogrn != null ? String(requisite.ogrn) : undefined,
        bankAccount: requisite?.bankAccount,
        bankName: requisite?.bankName,
        bik: requisite?.bik != null ? String(requisite.bik) : undefined,
        correspondentAccount: requisite?.correspondentAccount,
        address: requisite?.address,
        phone: requisite?.phone,
        email: requisite?.email,
        currency: requisite?.currency,
        billable: requisite?.billable ?? false,
      }}
    />
  );
}
